// Pure helpers for the Transfers screen (Branch Cash P4). The server decides everything; these only keep the form honest.

export const TRANSFER_STATUS_LABEL: Record<string, string> = {
  REQUESTED: 'Requested', DISPATCHED: 'In transit', RECEIVED: 'Received', DISCREPANCY: 'Discrepancy', RESOLVED: 'Resolved', CANCELLED: 'Cancelled', RETURNED: 'Returned to sender',
};
export const TRANSFER_STATUS_COLOR: Record<string, 'default' | 'info' | 'success' | 'warning' | 'error'> = {
  REQUESTED: 'default', DISPATCHED: 'info', RECEIVED: 'success', DISCREPANCY: 'error', RESOLVED: 'success', CANCELLED: 'default', RETURNED: 'warning',
};

export const isMoneyText = (v: string): boolean => /^\d+(\.\d{1,2})?$/.test(v.trim());
export const toPaise = (v: string): number => {
  const [w, f = ''] = v.trim().split('.');
  return Number(w) * 100 + Number(f.padEnd(2, '0'));
};

export function requestError(i: { from: string; to: string; amount: string; reason: string }): string {
  if (!i.from) return 'Choose the sending branch';
  if (!i.to) return 'Choose the receiving branch';
  if (i.from === i.to) return 'The two branches must be different';
  if (!isMoneyText(i.amount) || toPaise(i.amount) <= 0) return 'Enter an amount greater than zero (up to 2 decimals)';
  if (!i.reason.trim()) return 'Give a reason';
  return '';
}

export function dispatchError(requested: number, amount: string): string {
  if (amount.trim() === '') return '';
  if (!isMoneyText(amount) || toPaise(amount) <= 0) return 'Enter an amount greater than zero';
  if (toPaise(amount) > Math.round(requested * 100)) return 'Cannot exceed the requested amount';
  return '';
}

/** Mirrors the server: a different received amount is a discrepancy and needs an explanation; 0 is allowed. */
export function receiveHint(dispatched: number, received: string): { valid: boolean; discrepancy: number | null; needsExplanation: boolean } {
  if (!isMoneyText(received)) return { valid: false, discrepancy: null, needsExplanation: false };
  const diff = toPaise(received) - Math.round(dispatched * 100);
  return { valid: true, discrepancy: diff / 100, needsExplanation: diff !== 0 };
}
export const receiveError = (dispatched: number, received: string, explanation: string): string => {
  const h = receiveHint(dispatched, received);
  if (!h.valid) return 'Enter the amount actually received (0 if nothing arrived)';
  if (h.needsExplanation && !explanation.trim()) return 'The amount differs from what was sent - an explanation is required';
  return '';
};

export type TransferAction = 'dispatch' | 'receive' | 'resolve' | 'cancel' | 'return';

/** Return after dispatch is all-or-nothing: the full dispatched amount, with a reason. */
export const returnError = (dispatched: number, amount: string, reason: string): string => {
  if (!isMoneyText(amount) || toPaise(amount) <= 0) return 'Enter the amount of cash that came back (greater than zero)';
  if (toPaise(amount) > Math.round(dispatched * 100)) return 'Cannot exceed the dispatched amount';
  if (toPaise(amount) < Math.round(dispatched * 100)) return 'Return the full dispatched amount, or receive it at the destination and resolve the difference';
  if (!reason.trim()) return 'A reason is required';
  return '';
};

/**
 * Which action buttons make sense for a row, given status, the user's permissions and branch grants (UX only - the server enforces).
 * `me` (the signed-in user id) refines the person rules the server applies: cancel = requester or a dispatch/resolve holder;
 * return = not the person who dispatched; resolve = not the requester, dispatcher or receiver.
 */
export function availableActions(
  t: { status: string; fromBranchId: string; toBranchId: string; requestedById?: string; dispatchedById?: string | null; receivedById?: string | null },
  can: (perm: string) => boolean,
  granted: (branchId: string) => boolean,
  me?: string,
): TransferAction[] {
  const out: TransferAction[] = [];
  if (t.status === 'REQUESTED' && can('cash:transfer_dispatch') && granted(t.fromBranchId)) out.push('dispatch');
  if (t.status === 'DISPATCHED' && can('cash:transfer_receive') && granted(t.toBranchId)) out.push('receive');
  if (t.status === 'DISPATCHED' && can('cash:transfer_return') && granted(t.fromBranchId) && !(me && t.dispatchedById === me)) out.push('return');
  const independent = !me || (me !== t.requestedById && me !== t.dispatchedById && me !== t.receivedById);
  if (t.status === 'DISCREPANCY' && can('cash:transfer_resolve') && (granted(t.fromBranchId) || granted(t.toBranchId)) && independent) out.push('resolve');
  const manager = can('cash:transfer_dispatch') || can('cash:transfer_resolve');
  const mayCancel = me === undefined ? (can('cash:transfer_create') || manager) : ((t.requestedById === me && can('cash:transfer_create')) || manager);
  if (t.status === 'REQUESTED' && mayCancel && granted(t.fromBranchId)) out.push('cancel');
  return out;
}
